import { useEffect, useRef, useState } from "react";

/**
 * Simple canvas signature pad — mouse + touch. Returns a PNG data URL.
 * Props:
 *   onChange(dataUrl | null) — fires when a stroke ends; null when cleared
 *   width / height (default 400x140)
 */
export default function SignaturePad({ onChange, width = 400, height = 140, className = "" }) {
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    // scale for hi-dpi
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1a1a1a";
  }, [width, height]);

  const pt = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const touch = e.touches?.[0];
    return {
      x: (touch ? touch.clientX : e.clientX) - rect.left,
      y: (touch ? touch.clientY : e.clientY) - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = pt(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = pt(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    setDirty(true);
    onChange?.(ref.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setDirty(false);
    onChange?.(null);
  };

  return (
    <div className={className}>
      <canvas
        ref={ref}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full bg-white border border-border rounded-md touch-none cursor-crosshair"
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-muted-foreground">{dirty ? "Firma tracciata" : "Firma qui sopra"}</span>
        <button type="button" onClick={clear} className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-destructive btn-lift">
          Cancella
        </button>
      </div>
    </div>
  );
}
